import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import cls from 'classnames';

import noop from 'lodash/noop';
import qs from 'qs';

import { l10n } from '@ott/l10n';
import LoadingDots from '@ott/loading-dots';
import Modal, { CONTENT_SIZE } from '@ott/modal';
import {
  actions as virtualCardActions,
  selectors as virtualCardSelectors,
  hooks as virtualCardHooks,
} from '@ott/shared-virtual-card-logic';
import KM from '@ott/utility-km';

import ErrorModal from '../ErrorModal';
import IFrameModal from '../IFrameModal';
import SuccessModal from '../SuccessModal';
import RefillForm from './blocks/RefillForm';
import { getQueryParams } from './blocks/RefillForm/helpers/helpers';
import ThreeDSForm from './blocks/ThreeDsForm';
import { isThreeDSTwoAuth } from './blocks/ThreeDsForm/helpers/threeDSSteps';
import { getFormattedMoney } from './helpers/helpers';
import iframeDataAdapter from './helpers/iframeDataAdapter';
import {
  IframeDataResponseType,
  IframeDataAdaptedType,
  RefillFormData,
  ThreeDSAuthResult,
} from './types';

import styles from './RefillModal.scss';

const IFRAME_TARGET_NAME = 'send-frame';

const {
  virtualCardFlow: { useVirtualCardFlow },
} = virtualCardHooks;

const defaultIFrameModalSize = {
  width: 400,
  height: 620,
};

type Props = {
  amount?: number;
  isOpen?: boolean;
  onClose: VoidFunction;
  kmParams: Record<string, any>;
  isWebview?: boolean;
  successContentOptions?: {
    forceVerticalButtonLayout?: boolean;
    transparent?: boolean;
    size?: 'small' | 'medium' | 'wide';
    childrenClassNames?: {
      header?: string;
      closer?: string;
      footer?: string;
    };
  };
};

const RefillModal = ({
  amount = 0,
  isOpen = true,
  kmParams,
  onClose = noop,
  successContentOptions = {},
  isWebview = false,
}: Props) => {
  const [iframeData, setIframeData] = useState<IframeDataAdaptedType | null>(null);

  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const formData = useRef<RefillFormData | null>(null);

  const {
    auth: { isAuthorized },
  } = useVirtualCardFlow();

  const {
    data: virtualCardData,
    initial: isVirtualCardInitial,
    loading: isVirtualCardLoading,
  } = useSelector(virtualCardSelectors.getCardInfo);
  const { balance } = virtualCardData ?? {};

  const cardRefillData = useSelector(virtualCardSelectors.getCardRefill);
  const { data } = cardRefillData;
  const operationId = data ? data.operationId : null;

  const isVirtualCardInfoLoaded = !isVirtualCardInitial && !isVirtualCardLoading;
  const dispatch = useDispatch<any>();

  useEffect(() => {
    if (!isVirtualCardInfoLoaded && isAuthorized) {
      dispatch(virtualCardActions.getCardInfo());
    }
  }, []);

  useEffect(() => {
    KM('Virtual_Card_Replenish_Balance', { obj: kmParams });
  }, []);

  const handleError = useCallback(() => setIsError(true), []);

  const handleErrorModalClose = useCallback(() => {
    setIsError(false);
    onClose();
  }, [onClose]);

  const handleSuccessModalClose = useCallback(() => {
    setIsSuccess(false);
    onClose();
  }, [onClose]);

  const handleSuccess = useCallback(() => {
    setIsSuccess(true);
    dispatch(virtualCardActions.getCardInfo());
  }, [dispatch]);

  const handlePaySuccess = useCallback(() => {
    const isSavedCard = 'cardId' in (formData.current ?? {});

    KM('Virtual_Card_Replenish_Balance_Success', {
      obj: {
        ...kmParams,
        replenish_card: isSavedCard ? 'saved' : 'new',
        operation_id: operationId,
        platform: 'web',
      },
    });
    handleSuccess();
  }, [formData, handleSuccess, kmParams, operationId]);

  const handlePayError = useCallback(
    (errorCode: number | string | string[] | qs.ParsedQs | qs.ParsedQs[] | undefined) => {
      KM('Virtual_Card_Replenish_Balance_Failure', {
        obj: { ...kmParams, error: errorCode, operation_id: operationId },
      });
      handleError();
    },
    [handleError, kmParams, operationId]
  );

  const handleReceiveMessage = useCallback(
    (message: MessageEvent) => {
      const { type, event, search } = message.data;
      if (event === 'redirect' && type === 'result') {
        const { backRef } = getQueryParams();
        if (backRef) {
          // @ts-ignore
          window.location = backRef + search;

          return;
        }

        const { errorCode } = qs.parse(search, { ignoreQueryPrefix: true });

        if (errorCode) {
          handlePayError(errorCode);
        } else {
          handlePaySuccess();
        }
        setIframeData(null);
      }
    },
    [handlePayError, handlePaySuccess]
  );

  const handleThreeDsResult = useCallback(
    (threeDsResult: ThreeDSAuthResult) => {
      if (threeDsResult) {
        const { errorCode, result } = threeDsResult;
        if (errorCode) {
          handlePayError(errorCode);
          setIframeData(null);
        }

        if (!errorCode && result?.secureV2Content) {
          setIframeData(iframeDataAdapter(result.secureV2Content));
        }

        if (!errorCode && !result?.secureV2Content) {
          handlePaySuccess();
          setIframeData(null);
        }
      }
    },
    [handlePayError, handlePaySuccess]
  );

  const redirect = (iframe: IframeDataResponseType, form: RefillFormData) => {
    setIframeData(iframeDataAdapter(iframe));
    formData.current = form;
  };

  if (isError) {
    return (
      <ErrorModal
        isOpen={isOpen}
        title={l10n('virtualCard.errors.refillError.title')}
        description={l10n('virtualCard.errors.refillError.description')}
        actionButtonText={l10n('virtualCard.errors.refillError.action')}
        actionButtonPurpose="neutral"
        onClose={handleErrorModalClose}
        onAction={handleErrorModalClose}
      />
    );
  }

  if (iframeData) {
    return (
      <>
        <ThreeDSForm
          onError={handleError}
          onResult={handleThreeDsResult}
          iframeData={iframeData}
          submitTarget={IFRAME_TARGET_NAME}
        />

        <IFrameModal
          name={IFRAME_TARGET_NAME}
          className={cls(styles.iframeModal, {
            [styles.webview]: isWebview,
          })}
          isOpen={isOpen}
          onClose={onClose}
          onMessage={handleReceiveMessage}
          defaultSize={defaultIFrameModalSize}
          isWebview={isWebview}
          isHidden={Boolean(isThreeDSTwoAuth(iframeData))}
        />
      </>
    );
  }

  if (isSuccess) {
    const formattedAmount = getFormattedMoney(Number(formData.current?.amount) ?? 0);
    const formattedBalance = getFormattedMoney(balance ?? 0);

    return (
      <SuccessModal
        isOpen={isOpen}
        title={l10n('virtualCard.refillSuccess.title')}
        description={
          <div>
            <p className={styles.income}>+{formattedAmount}</p>
            <p className={styles.balance}>
              {balance ? (
                l10n('virtualCard.refillSuccess.description', { balance: formattedBalance })
              ) : (
                <LoadingDots />
              )}
            </p>
          </div>
        }
        actionButtonText={l10n('virtualCard.refillSuccess.action')}
        actionButtonPurpose="neutral"
        onAction={handleSuccessModalClose}
        onClose={handleSuccessModalClose}
        customContentOptions={successContentOptions}
      />
    );
  }

  const modalContentOptions = {
    title: !isWebview ? l10n('virtualCard.refillForm.title') : '',
    hideClose: isWebview,
    size: CONTENT_SIZE.SMALL,
    className: cls(styles.content, { [styles.webview]: isWebview }),
    childrenClassNames: {
      body: styles.body,
    },
  };

  return (
    <Modal
      isOpen={isOpen}
      fullScreenOnMobile={true}
      contentOptions={modalContentOptions}
      specificLocators="refill-modal"
      onClose={onClose}
    >
      <RefillForm redirect={redirect} amount={amount ? Math.ceil(amount).toString() : ''} />
    </Modal>
  );
};

export default React.memo(RefillModal);
